import * as Inovx from '@shared';
import ViewerHelpers from '../helpers/ViewerHelpers';
import MeasurementServices from '../services/MeasurementServices';
import AssemblyServices from '../services/AssemblyServices';
import FacilityStore from '../stores/FacilityStore';
import FacilityActions from './FacilityActions';
import ComponentHelpers from '../helpers/ComponentHelpers';
import { KNOWLEDGE_VIEW_EDIT_SESSION_CHANGED, KnowledgeViewEditSessionGuard, canSaveMeasurement, canPersistMeasurementDeletion, type EditContinuation } from '../helpers/knowledgeViewEditSession';
import ViewerStore from '../stores/ViewerStore';
import AnnotationActions from './AnnotationActions';
import AssetActions from './AssetActions';
import KnowledgeBookActions from './KnowledgeBookActions';
import FacilityConstants from '../constants/FacilityConstants';
import HierarchyItemServices from '../services/HierarchyItemServices';
import { ContentLayers, IHierarchyItemDetail, KNOWLEDGE_VIEW_CONTENT_LAYERS_VERSION } from '../Interfaces/ViewerInterfaces';
import * as Interfaces from '../Interfaces/Interfaces';
import * as Enumerations from '../Enumerations/Enumerations';
import ReactViewer3DActions from '../viewers/lodviewer/ReactViewer3DActions';
import { PositionDataModel } from '@syncfusion/ej2-react-popups';
import AssetSetsServices from '../services/AssetSetsServices';
import KnowledgeBookStore from '../stores/KnowledgeBookStore';
import ViewerConstants from './../constants/ViewerConstants';
import AssetSetMembershipCache from '../helpers/assetSetMembershipCache';
import { loadInBoundedBatches } from '../helpers/assetSetMembershipBatchLoader';
import { getAssetSetPopulationSignature, planAssetSetMembershipDelta } from '../helpers/viewerAssetSetModel';
import type { RepeatedSelectionBehavior } from '../helpers/viewerSelectionModel';
import { buildAssetSetDisplayDefinitions, cloneDisplayConfiguration, normalizeDisplayID, setDisplayEntryMode } from '../helpers/assetSetVisualizationModel';
import AssetSetVisualizationStore from '../stores/AssetSetVisualizationStore';
import FacilityAssetSetStore from '../stores/FacilityAssetSetStore';
import { mergeViewerAssetSets } from '../helpers/viewerAssetSetModel';
import { toKnowledgeViewAssetSets } from '../helpers/knowledgeViewVisualizationModel';
var SystemActions = Inovx.Shared.System.Actions;
var SystemStore = Inovx.Shared.System.Store;

const KNOWLEDGE_VIEW_THUMBNAIL_WIDTH = 1200;
const KNOWLEDGE_VIEW_THUMBNAIL_HEIGHT = 675;

export interface VisualizationKnowledgeViewCreateInput {
    knowledgeBook: IKnowledgeBook;
    name: string;
    description: string;
    configuration: IAssetSetDisplayConfiguration;
}

export interface VisualizationKnowledgeViewCreateResult {
    knowledgeBook: IKnowledgeBook;
    knowledgeView: IKnowledgeView;
    displayPrioritySaved: boolean;
    expectedAssetSets: IKnowledgeViewAssetSet[];
}

function isHandledApiError(error: unknown): boolean {
    if (!error || typeof error !== 'object') return false;

    const candidate = error as { status?: unknown; responseText?: unknown; messageObj?: unknown };
    return typeof candidate.status === 'number'
        && ('responseText' in candidate || 'messageObj' in candidate);
}

class ViewerActions {
    private readonly _editSession = new KnowledgeViewEditSessionGuard({
        getID: () => ViewerStore.getKnowledgeViewEditID(),
        isDirty: () => ViewerStore.isKnowledgeViewDirty(),
        setBusy: busy => this._dispatchAction(KNOWLEDGE_VIEW_EDIT_SESSION_CHANGED, {
            id: ViewerStore.getKnowledgeViewEditID(), busy,
        }),
        persist: resolution => resolution === 'save' ? this.saveKnowledgeViewChanges() : this.resetKnowledgeViewChanges(),
        finish: () => this.finishKnowledgeViewEditSession(),
        prompt: (save, discard) => SystemActions.displaySaveChangesDialog({
            message: InovxLoader.t('facility:message.saveKVChanges'),
            confirmCallback: save,
            cancelCallback: discard,
        }),
        onError: error => {
            console.error('Unable to resolve Knowledge View editing.', error);
            if (!isHandledApiError(error)) {
                SystemActions.displayWarningMessage({ message: InovxLoader.t('facility:message.knowledgeViewEditFailed') });
            }
        },
    });

    beginKnowledgeViewEditSession() {
        const kv = ViewerStore.getSelectedKnowledgeView();
        if (!kv || kv.KnowledgeBookID !== KnowledgeBookStore.getSelectedKnowledgeBook()?.ID || ViewerStore.isKnowledgeViewEditBusy() ||
            !ComponentHelpers.hasModifyKnowledgeBookPermission(KnowledgeBookStore.getSelectedKnowledgeBook())) return;
        this._dispatchAction(KNOWLEDGE_VIEW_EDIT_SESSION_CHANGED, { id: kv.ID, busy: false });
        FacilityActions.openKnowledgeViewEditBar();
    }

    private finishKnowledgeViewEditSession() {
        this._dispatchAction(KNOWLEDGE_VIEW_EDIT_SESSION_CHANGED, { id: null, busy: false });
        const mode = ViewerStore.getDisplayMode();
        if (mode === DisplayMode.CreateAnnotation || mode === DisplayMode.CreateAssembly || mode === DisplayMode.PositionAnnotation) this.resetDisplayMode();
        this._setSelectedAssemblyCreationTemplate(null);
        this.setAssemblyTemplate(null);
        FacilityActions.toggleAssemblyLibraryBar(false);
        if (FacilityStore.getKnowledgeViewEditBarOpen()) {
            FacilityActions.closeKnowledgeViewEditBar();
            FacilityActions.openKBbar();
        }
    }

    saveKnowledgeViewEditSession() {
        return this._editSession.complete('save');
    }

    cancelKnowledgeViewEditSession() {
        return this._editSession.complete('discard');
    }

    canAuthorKnowledgeView() {
        return ViewerStore.isEditingKnowledgeView() && !ViewerStore.isKnowledgeViewEditBusy();
    }

    /**
     * Facility authoring remains available outside a selected Knowledge View.
     * Once a Knowledge View is selected, its edit session controls authoring.
     */
    canAuthorFacilityContent() {
        return !ViewerStore.getSelectedKnowledgeView() || this.canAuthorKnowledgeView();
    }
    private _viewerId : string;
    private _panoScreenshotProvider: ((width: number, height: number) => Promise<string | null>) | null;
    private readonly _assetSetMembershipCache: AssetSetMembershipCache;
    private _assetSetPopulationGeneration: number;
    private readonly _activeAssetSetMemberships: Map<string, { id: string; signature: string }>;
    private _assetSetMembershipFacilityID: string | null;

    constructor() {
        this._panoScreenshotProvider = null;
        this._assetSetMembershipCache = new AssetSetMembershipCache();
        this._assetSetPopulationGeneration = 0;
        this._activeAssetSetMemberships = new Map();
        this._assetSetMembershipFacilityID = null;
    }

    setViewerId(viewerId: string) {
        if (this._viewerId !== viewerId) {
            this._assetSetMembershipCache.clear();
            this._assetSetPopulationGeneration++;
            this._activeAssetSetMemberships.clear();
            this._assetSetMembershipFacilityID = null;
        }
        this._viewerId = viewerId;
    }

    setPanoScreenshotProvider(provider: ((width: number, height: number) => Promise<string | null>) | null) {
        this._panoScreenshotProvider = provider;
    }

    showPanoViewerImage(view: IView360Detail, focusedAnnotation?: IAnnotationDisplayItem, openInPano: boolean = true) {
        if (this.checkForKnowledgeViewChanges(() => { this.showPanoViewerImage(view, focusedAnnotation, openInPano); })) return false;
        if (openInPano)
            this.setOpenPointCloud(false, true);

        this.resetDisplayMode();

        this._dispatchAction(ViewerConstants.VIEWER_SHOW_IMAGE, { view, focusedAnnotation });
        return true;
    }

    showOntologyViewer(ontology: IOntologyPart) {

        this.resetDisplayMode();

        this._dispatchAction(ViewerConstants.VIEWER_SHOW_ONTOLOGY_3D, { ontology });
        return true;
    }


    hidePanoViewerImage() {
        this._dispatchAction(ViewerConstants.VIEWER_HIDE_IMAGE);
    }

    resetCameraProperties() {
        this._dispatchAction(ViewerConstants.VIEWER_RESET_CAMERA_PROPERTIES);
    }
    async openKnowledgeView(kvID: string, lodData?: string) {
        const selectedKV = ViewerStore.getSelectedKnowledgeView();

        if (selectedKV?.ID != kvID) {
            if (this.checkForKnowledgeViewChanges(() => this.openKnowledgeView(kvID, lodData))) return;
            await this._openKnowledgeViewInternal(kvID, lodData);
        }
    }

    setKnowledgeViewDirty(dirty: boolean) {
        this._dispatchAction(ViewerConstants.KNOWLEDGE_VIEW_SET_DIRTY, dirty);
    }

    focusAnnotation(annotation: IAnnotationDisplayItem) {
        this._dispatchAction(ViewerConstants.VIEWER_FOCUS_ANNOTATION, { annotation });
    }

    focusMeasurement(measurement: Measurement) {
        this._dispatchAction(ViewerConstants.VIEWER_FOCUS_MEASUREMENT, { measurement });
    }

    updateMeasurements(measurements: Measurement[]) {
        this._dispatchAction(ViewerConstants.VIEWER_UPDATE_MEASUREMENTS, measurements);
    }

    closeKnowledgeView(saveForRestore: boolean = false) {
        
        this._dispatchAction(ViewerConstants.KNOWLEDGE_VIEW_CLOSE, { saveForRestore });
        this.updateMeasurements([]);
        this.createAssemblyInstances(FacilityStore.getSelectedFacility().Assemblies);
    }

    setCameraAsDirty() {
        this._dispatchAction(ViewerConstants.CAMERA_SET_DIRTY, true);
    }
    resetCameraAsDirty() {
        this._dispatchAction(ViewerConstants.CAMERA_SET_DIRTY, false);
    }
    updateViewerDirection(source : any, direction: any) {
        this._dispatchAction(ViewerConstants.UPDATE_VIEWER_DIRECTION, { source: source, direction: direction });
    }
    setViewerContentTypes(types: string[]) {
        this._dispatchAction(ViewerConstants.VIEWER_CONTENT_TYPES, types);
    }
    private _getCurrentKnowledgeViewDisplayProperties(): IDisplayProperties {
        const contentLayers: ContentLayers = ViewerStore.getContentLayers();
        const layers = (Object.keys(contentLayers) as (keyof ContentLayers)[])
            .filter(k => !!contentLayers[k])
            .map(k => k as string);
        const visibleAssetTagClasses = ViewerStore.getVisibleAssetTagClasses();
        const advancedMaterialTunings = ViewerStore.getSelectedKnowledgeView()
            ?.DisplayProperties?.AdvancedMaterialTunings;

        if (ViewerStore.is3DViewerVisible()) {
            const cameraProps = ViewerStore.getViewer3DCameraProperties();
            const viewerProps = ViewerStore.getViewerRuntimeSettings();

            const pos = cameraProps?.position;
            const tgt = cameraProps?.target;

            const runtimeSettingsJson = viewerProps;

            return {
                CameraPosition: pos ? { X: pos.X, Y: pos.Y, Z: pos.Z } : null,
                CameraTarget: tgt ? { X: tgt.X, Y: tgt.Y, Z: tgt.Z } : null,
                ContentLayers: layers,
                ContentLayersVersion: KNOWLEDGE_VIEW_CONTENT_LAYERS_VERSION,
                VisibleAssetTagClasses: visibleAssetTagClasses,
                DisplayObstructedTags: ViewerStore.getDisplayObstructedTags(),
                FilterOverlapping360Positions: ViewerStore.getFilterOverlapping360Positions(),
                ViewerSettings: runtimeSettingsJson,
                AdvancedMaterialTunings: advancedMaterialTunings
            }; 
        } else {
            const cameraProperties = ViewerStore.getViewerCameraProperties();
            return {
                Yaw: cameraProperties.yaw,
                Pitch: cameraProperties.pitch,
                Fov: cameraProperties.fov,
                ContentLayers: layers,
                ContentLayersVersion: KNOWLEDGE_VIEW_CONTENT_LAYERS_VERSION,
                VisibleAssetTagClasses: visibleAssetTagClasses,
                DisplayObstructedTags: ViewerStore.getDisplayObstructedTags(),
                FilterOverlapping360Positions: ViewerStore.getFilterOverlapping360Positions(),
                AdvancedMaterialTunings: advancedMaterialTunings
            };
        }
    }

    async createNewKnowledgeView(book: IKnowledgeBook) {
        let name: string = '';
        let viewId: string = null;
        let type: number = 1;
        let ontologyId: string = null;
        let displayProperties = this._getCurrentKnowledgeViewDisplayProperties();

        if (ViewerStore.is3DViewerVisible()) {
            // If this is the first time a 3D knowledge view is being created for this knowledge book,
            // ensure that the knowledge book has the ontology ID set.
            if (!book.OntologyID) {
                const ontology = FacilityStore.getSelectedOntology();
                book.OntologyID = ontology.ID;

                book = await KnowledgeBookActions.editKnowledgeBook(book);
            }

            name = `3D Knowledge View`;
            type = 2;
            ontologyId = book.OntologyID;
        } else {
            const cameraProperties = ViewerStore.getViewerCameraProperties();
            const view = ViewerStore.getSelectedViewImage();
            name = `${view.Name} KV`;
            viewId = view.ID;
        }
       
        var kv: IKnowledgeViewDetail = {
            KVImage: '',
            ID: null,
            VersionToken: null,
            Name: name,
            KnowledgeBookID: book.ID,
            ViewID: viewId,
            Tags: [],
            DisplayProperties: displayProperties,
            View: null,
            Type: type,
            OntologyID: ontologyId
        };

        return await KnowledgeBookActions.createKnowledgeView(kv);
    }

    async createKnowledgeViewFromVisualization(
        input: VisualizationKnowledgeViewCreateInput,
    ): Promise<VisualizationKnowledgeViewCreateResult> {
        const is3DViewer = ViewerStore.is3DViewerVisible();
        let knowledgeBook = { ...input.knowledgeBook };
        let viewID: string = null;
        let ontologyID: string = null;

        if (is3DViewer) {
            ontologyID = FacilityStore.getSelectedOntology()?.ID;
            if (!ontologyID || (knowledgeBook.OntologyID && knowledgeBook.OntologyID !== ontologyID)) {
                throw new Error('The selected Knowledge Book is not compatible with the current 3D ontology.');
            }
            if (!knowledgeBook.OntologyID) {
                const updatedBook = await KnowledgeBookActions.editKnowledgeBook({
                    ...knowledgeBook,
                    OntologyID: ontologyID,
                });
                if (!updatedBook) throw new Error('The Knowledge Book ontology could not be updated.');
                knowledgeBook = updatedBook;
            }
        } else {
            const view = ViewerStore.getSelectedViewImage();
            if (!view?.ID) throw new Error('A Pano view must be selected before creating a Knowledge View.');
            viewID = view.ID;
        }

        let thumbnail: Blob | null = null;
        try {
            thumbnail = await this._createKnowledgeViewThumbnail();
        } catch {
            // A missing thumbnail must not prevent the Knowledge View from being created.
        }

        const configuration = cloneDisplayConfiguration(input.configuration);
        const isPano = !is3DViewer;
        const expectedAssetSets = toKnowledgeViewAssetSets(configuration, isPano);
        const knowledgeView: IKnowledgeViewDetail = {
            KVImage: '',
            ID: null,
            VersionToken: null,
            Name: input.name.trim(),
            Description: input.description || '',
            KnowledgeBookID: knowledgeBook.ID,
            ViewID: viewID,
            Tags: [],
            DisplayProperties: this._getCurrentKnowledgeViewDisplayProperties(),
            View: null,
            Type: is3DViewer ? 2 : 1,
            OntologyID: ontologyID,
            ThumbnailImageFile: thumbnail,
        };

        const createdKnowledgeView = await KnowledgeBookActions.createKnowledgeView(knowledgeView, {
            initializeDefaultAssetSets: false,
            refreshStores: false,
        });

        let savedKnowledgeView: IKnowledgeView = createdKnowledgeView;
        let displayPrioritySaved = false;
        try {
            const detail = await KnowledgeBookActions.getKnowledgeViewDetail(createdKnowledgeView.ID);
            detail.AssetSets = expectedAssetSets;
            savedKnowledgeView = await KnowledgeBookActions.updateKnowledgeView(detail, { refreshStore: false });
            displayPrioritySaved = true;
        } catch (error) {
            console.error('Knowledge View created, but its display priority could not be saved.', error);
        }

        try {
            await KnowledgeBookActions.refreshCurrentKnowledgeBooks();
        } catch (error) {
            console.error('Knowledge View created, but the Knowledge Book list could not be refreshed.', error);
        }

        return {
            knowledgeBook,
            knowledgeView: savedKnowledgeView,
            displayPrioritySaved,
            expectedAssetSets,
        };
    }

    private async _createKnowledgeViewThumbnail(): Promise<Blob | null> {
        const thumbnailDataUrl = ViewerStore.is3DViewerVisible()
            ? await this._create3DKnowledgeViewThumbnail()
            : await this._createPanoKnowledgeViewThumbnail();

        if (!thumbnailDataUrl) {
            return null;
        }

        return this._dataUrlToBlob(thumbnailDataUrl);
    }

    private async _create3DKnowledgeViewThumbnail(): Promise<string | null> {
        if (!this._viewerId) {
            return null;
        }

        const image = await ReactViewer3DActions.getScreenshot(
            this._viewerId,
            KNOWLEDGE_VIEW_THUMBNAIL_WIDTH,
            KNOWLEDGE_VIEW_THUMBNAIL_HEIGHT,
            false
        );
        return image || null;
    }

    private async _createPanoKnowledgeViewThumbnail(): Promise<string | null> {
        if (this._panoScreenshotProvider) {
            try {
                const image = await this._panoScreenshotProvider(
                    KNOWLEDGE_VIEW_THUMBNAIL_WIDTH,
                    KNOWLEDGE_VIEW_THUMBNAIL_HEIGHT
                );

                if (image) {
                    return image;
                }
            }
            catch {
                // Fall back to the live canvas capture when the pano viewer screenshot path is unavailable.
            }
        }

        return this._createCanvasKnowledgeViewThumbnail();
    }

    private _createCanvasKnowledgeViewThumbnail(): string | null {
        const viewerCanvas = document.querySelector('#pano-viewer canvas');
        if (!(viewerCanvas instanceof HTMLCanvasElement) || viewerCanvas.width === 0 || viewerCanvas.height === 0) {
            return null;
        }

        const thumbnailCanvas = document.createElement('canvas');
        thumbnailCanvas.width = KNOWLEDGE_VIEW_THUMBNAIL_WIDTH;
        thumbnailCanvas.height = KNOWLEDGE_VIEW_THUMBNAIL_HEIGHT;

        const context = thumbnailCanvas.getContext('2d');
        if (!context) {
            return null;
        }

        const targetAspectRatio = KNOWLEDGE_VIEW_THUMBNAIL_WIDTH / KNOWLEDGE_VIEW_THUMBNAIL_HEIGHT;
        const sourceAspectRatio = viewerCanvas.width / viewerCanvas.height;

        let sourceX = 0;
        let sourceY = 0;
        let sourceWidth = viewerCanvas.width;
        let sourceHeight = viewerCanvas.height;

        if (sourceAspectRatio > targetAspectRatio) {
            sourceWidth = viewerCanvas.height * targetAspectRatio;
            sourceX = (viewerCanvas.width - sourceWidth) / 2;
        } else if (sourceAspectRatio < targetAspectRatio) {
            sourceHeight = viewerCanvas.width / targetAspectRatio;
            sourceY = (viewerCanvas.height - sourceHeight) / 2;
        }

        context.drawImage(
            viewerCanvas,
            sourceX,
            sourceY,
            sourceWidth,
            sourceHeight,
            0,
            0,
            KNOWLEDGE_VIEW_THUMBNAIL_WIDTH,
            KNOWLEDGE_VIEW_THUMBNAIL_HEIGHT
        );
        return thumbnailCanvas.toDataURL('image/png');
    }

    private _dataUrlToBlob(dataUrl: string): Blob | null {
        const imageParts = dataUrl.split(',');
        if (imageParts.length < 2) {
            return null;
        }

        const mimeMatch = imageParts[0].match(/data:(.*?);base64/);
        const mimeType = mimeMatch?.[1] || 'image/png';
        const binaryString = atob(imageParts[1]);
        const bytes = new Uint8Array(binaryString.length);

        for (let index = 0; index < binaryString.length; index++) {
            bytes[index] = binaryString.charCodeAt(index);
        }

        return new Blob([bytes], { type: mimeType });
    }

    async saveKnowledgeViewChanges() {
        const kv = ViewerStore.getSelectedKnowledgeView();
        kv.DisplayProperties = this._getCurrentKnowledgeViewDisplayProperties();
        kv.ThumbnailImageFile = await this._createKnowledgeViewThumbnail();
       
        const kvRes = await KnowledgeBookActions.updateKnowledgeView(kv);

        // Refresh the selected knowledge view in the viewer store (versionToken most importantly)
        Inovx.Shared.Flux.AppDispatcher.dispatch({
            actionType: ViewerConstants.KNOWLEDGE_VIEW_SET_DATA,
            data: {kv: kvRes}
        });
    }

    async resetKnowledgeViewChanges() {
        const kv = ViewerStore.getSelectedKnowledgeView();
        await this._openKnowledgeViewInternal(kv.ID);
    }

    setSelectedKnowledgeViewData(kvDetail: IKnowledgeViewDetail) {
        Inovx.Shared.Flux.AppDispatcher.dispatch({
            actionType: ViewerConstants.KNOWLEDGE_VIEW_SET_DATA,
            data: { kvDetail }
        });
        return kvDetail;
    }

    resetKnowledgeViewDisplayPriorities() {
        this._dispatchAction(ViewerConstants.KNOWLEDGE_VIEW_RESET_DISPLAY_PRIORITIES);
    }

    setCameraProperties(yaw: number, pitch: number, fov: number) {
        this._dispatchAction(ViewerConstants.VIEWER_SET_CAMERA_PROPERTIES, { yaw, pitch, fov });
    }
    setRangePosition(position:IVector3D) {
        this._dispatchAction(ViewerConstants.VIEWER_SET_RANGE_POSITION, { position });
    }
    updateRangePosition(position: IVector3D) {
        this._dispatchAction(ViewerConstants.VIEWER_UPDATE_RANGE_POSITION, { position });
    }
    exitRangeView() {
        this._dispatchAction(ViewerConstants.VIEWER_EXIT_RANGE_VIEW);
    }
    setDisplayObstructedTags(display: boolean) {
        this._dispatchAction(ViewerConstants.VIEWER_SET_DISPLAY_OBSTRUCTED_TAGS, display);
    }
    setFilterOverlapping360Positions(filter: boolean) {
        this._dispatchAction(ViewerConstants.VIEWER_SET_FILTER_OVERLAPPING_360_POSITIONS, filter);
    }
    setVisibleAssetTagClasses(assetClassIDs: string[] | null) {
        this._dispatchAction(ViewerConstants.VIEWER_SET_VISIBLE_ASSET_TAG_CLASSES, assetClassIDs);
    }
    toggleVisibleAssetTagClass(assetClassID: string, availableAssetClassIDs?: string[]) {
        const layers = ViewerStore.getContentLayers();
        const current = layers.assetTags ? ViewerStore.getVisibleAssetTagClasses() : [];
        const facility = FacilityStore.getSelectedFacility();
        const selectedOntologyID = ViewerStore.isPanoViewerVisible()
            ? FacilityStore.getRootOntologyPart()?.ID
            : FacilityStore.getSelectedOntology()?.ID;
        const uniqueClassIDs = availableAssetClassIDs ?? ViewerHelpers.getDisplayableAssetTagClassIDs(
            facility?.AssetTags ?? [],
            facility,
            selectedOntologyID,
            ViewerStore.getFilteredAssets() ?? []
        );
        const visibleClassIDs = current ? [...current] : uniqueClassIDs;

        const next = visibleClassIDs.includes(assetClassID)
            ? visibleClassIDs.filter(id => id !== assetClassID)
            : [...visibleClassIDs, assetClassID];

        const allAvailableClassesVisible = uniqueClassIDs.every(id => next.includes(id));
        this.setVisibleAssetTagClasses(allAvailableClassesVisible ? null : next);
        this.setContentLayers({ ...layers, assetTags: next.length > 0 });
    }

    setCreateAnnotationDisplayMode(annotationType: AnnotationType, annotationClassID?: string) {
        if (!this.canAuthorFacilityContent()) return false;
        let canSetMode = ViewerStore.is3DViewerVisible() || ViewerStore.isOpenPointCloud() || this._checkDepthMapLoaded();
        if (canSetMode) {
            this.resetAnnotation();
            this.resetHierarchyItemSelection();
            this.resetMeshItemSelection();
            this.resetAssemblySelection();
            ReactViewer3DActions.highlightAssets(this._viewerId, []);
            this._setDisplayMode(DisplayMode.CreateAnnotation, annotationType, null, null, annotationClassID);
        }
        return canSetMode;
    }

    displayAllAssetTags() {
        const knowledgeView = ViewerStore.getSelectedKnowledgeView();
        const facility = FacilityStore.getSelectedFacility();
        const allAssetTags = FacilityAssetSetStore.getAssetSets(facility?.ID)
            .find(assetSet => assetSet.Type === AssetSetType.AllAssetTags);

        // When authoring a Knowledge View, make the special All Asset Tags
        // entry visible if it is currently hidden (including its implicit
        // hidden state when the entry is absent). Preserve any saved color.
        if (knowledgeView && allAssetTags && this.canAuthorKnowledgeView()) {
            const currentAssetSets = knowledgeView.AssetSets ?? [];
            const allAssetTagsKey = allAssetTags.ID.toLocaleLowerCase();
            const currentEntry = currentAssetSets.find(entry => entry.ID?.toLocaleLowerCase() === allAssetTagsKey);

            if (!currentEntry || currentEntry.Hidden) {
                const nextAssetSets = currentEntry
                    ? currentAssetSets.map(entry => entry.ID?.toLocaleLowerCase() === allAssetTagsKey
                        ? { ...entry, Hidden: false }
                        : entry)
                    : [
                        ...currentAssetSets,
                        {
                            ID: allAssetTags.ID,
                            Ord: Math.max(0, ...currentAssetSets.map(entry => entry.Ord ?? 0)) + 1,
                            Hidden: false,
                        },
                    ];

                this.setSelectedKnowledgeViewData({
                    ...knowledgeView,
                    AssetSets: nextAssetSets,
                });
            }
        }

        // Display & Visualization owns the facility-level fallback configuration when
        // no Knowledge View is selected. Reveal All Asset Tags only when that entry is
        // explicitly hidden, preserving its configured color and every other entry.
        if (!knowledgeView) {
            const configuration = AssetSetVisualizationStore.getConfiguration();
            const allAssetTagsEntry = configuration?.Entries.find(entry =>
                normalizeDisplayID(entry.AssetSetID) === normalizeDisplayID(configuration.AllAssetTagsID));
            if (configuration && allAssetTagsEntry?.Mode === 'hidden') {
                AssetSetVisualizationStore.update(
                    setDisplayEntryMode(configuration, configuration.AllAssetTagsID, 'default')
                );
            }
        }

        // Reveal tags in this viewer and enable the layer without changing
        // already-visible display-priority entries.
        this._dispatchAction(ViewerConstants.VIEWER_DISPLAY_ALL_ASSET_TAGS);
        this.setLayerVisible('assetTags');
    }

    hasHiddenAssetTags(): boolean {
        if (!ViewerStore.getContentLayers().assetTags || ViewerStore.getVisibleAssetTagClasses() !== null) return true;
        if (ViewerStore.getDisplayAllAssetTags()) return false;

        const facility = FacilityStore.getSelectedFacility();
        const assetSets = mergeViewerAssetSets(
            KnowledgeBookStore.getAllAssetSet(),
            FacilityAssetSetStore.getAssetSets(facility?.ID)
        );
        const knowledgeView = ViewerStore.getSelectedKnowledgeView();
        if (knowledgeView) {
            const resolution = ViewerHelpers.resolveKnowledgeViewAssetTags(
                knowledgeView.AssetSets, assetSets, [], ViewerStore.isPanoViewerVisible()
            );
            return !resolution.fallbackVisible || resolution.hiddenTagIDs.length > 0;
        }

        const definitions = buildAssetSetDisplayDefinitions(
            AssetSetVisualizationStore.getConfiguration(),
            new Map(assetSets.map(assetSet => [normalizeDisplayID(assetSet.ID), assetSet]))
        );
        const resolution = ViewerHelpers.resolveAssetSetDisplayTags(definitions);
        return resolution.fallbackHidden || resolution.hiddenTagIDs.length > 0;
    }

    setPositionAnnotationDisplayMode() {
        if (!this.canAuthorFacilityContent()) return false;
        let canSetMode = ViewerStore.is3DViewerVisible() || ViewerStore.isOpenPointCloud() || this._checkDepthMapLoaded();
        if (canSetMode) {
            this._setDisplayMode(DisplayMode.PositionAnnotation);
        }
        return canSetMode;
    }
   
    async setSelectedAssemblyTemplate(assemblyTemplate: IAssemblyTemplate) {
        if (assemblyTemplate && !this.canAuthorFacilityContent()) return;
        // Since template selection AND selected instance both have a viewer state, reset selection
        // of instance when template changes.
        if (ViewerStore.getSelectedAssembly() != null) this.resetAssemblySelection();
        this._setSelectedAssemblyCreationTemplate(assemblyTemplate);
    }
    setAssemblyCreateMode(assemblyID: string) {
        if (!this.canAuthorFacilityContent()) return false;
        let canSetMode = ViewerStore.isOpenPointCloud();
        if (canSetMode) {

            this._setDisplayMode(DisplayMode.CreateAssembly, null, null, assemblyID);
        }
        return canSetMode;
    }
    setMeasurementCreateMode(mode: MeasurementType) {
        let canSetMode = ViewerStore.is3DViewerVisible() || ViewerStore.isOpenPointCloud() || this._checkDepthMapLoaded();
        if (canSetMode) {
            this._setDisplayMode(DisplayMode.Measurement, null, mode);
        }
        return canSetMode;
    }
    setPointCloudLoaded(loaded: boolean) {
        this._dispatchAction(ViewerConstants.VIEWER_SET_POINT_CLOUD_LOADED, loaded);
    }
    setOpenPointCloud(open: boolean, resetDirectionSource: boolean) {
        this.resetDisplayMode();
        // set the camera direction before opening/closing point cloud viewer.
        this._dispatchAction(ViewerConstants.VIEWER_SET_OPEN_POINT_CLOUD, { open: open, resetDirectionSource: resetDirectionSource } );
    }
    setPointCloudData(data: BABYLON.Mesh[]) {
        this._dispatchAction(ViewerConstants.VIEWER_SET_POINT_CLOUD_DATA, data);
    }
    setVolumeChangeDisplayMode(changeType: "tagmove" | "tagscale" | "assemblymove" | "assemblyscale") {
        if (!this.canAuthorFacilityContent()) return false;
        let canSetMode = ViewerStore.is3DViewerVisible() || ViewerStore.isOpenPointCloud() || this._checkDepthMapLoaded();
        if (canSetMode) {
            this._setDisplayMode(changeType == "tagmove" ? DisplayMode.TagVolumeMove :
                changeType == "tagscale" ? DisplayMode.TagVolumeScale :
                    changeType == "assemblymove" ? DisplayMode.AssemblyVolumeMove :
                        DisplayMode.AssemblyVolumeScale);
        }
        return canSetMode;
    }

    resetDisplayMode() {
        this._setDisplayMode(DisplayMode.Navigation, null, null, "");
        this._setSelectedAssemblyCreationTemplate(null);
        this.resetAssemblySelection();
    }

    setOpenSpeedDial(name: string | null) {
        this._dispatchAction(ViewerConstants.VIEWER_SET_SPEEDDIAL, name);
    }

    async createAnnotation(target3D: IVector3D) {
        if (!this.canAuthorKnowledgeView()) return;

        const facility = FacilityStore.getSelectedFacility();
        //const view = ViewerStore.getSelectedViewImage();
        const annotationType = ViewerStore.getAnnotationType();
        const knowledgeView = ViewerStore.getSelectedKnowledgeView();
        const selectedAnnotationClassID = ViewerStore.getSelectedAnnotationClassID();
        const classNameLow = this._getClassNameLower(annotationType);
        const assetClass = selectedAnnotationClassID
            ? facility.AnnotationClasses.find(ac => ac.ID === selectedAnnotationClassID)
            : facility.AnnotationClasses.find(ac => ac.Name.toLowerCase() == classNameLow);
        if (!assetClass) {
            throw new Error('Annotation class not found for selected annotation mode.');
        }
        let text: string;
        if (annotationType == AnnotationType.LinkAnnotation) {
            text = JSON.stringify({ DisplayText: "New Tag", Link: "" });
        } else {
            text = "New Tag";
        }

        const annotation: IViewAnnotationTag = {
            ID: null, // not used on create
            VersionToken: null, // not used on create
            FacilityID: facility.ID,
            AssetClassID: assetClass.ID,
            //View360ID: view.ID,
            KnowledgeViewID: knowledgeView.ID,
            Text: text,
            Target2D: null,
            Target3D: target3D,
            TargetBoxMin: null,
            TargetBoxMax: null,

            //key: "",
            AnnotationType: annotationType,
            AssetClass: assetClass
        }

        await AnnotationActions.createAnnotation(annotation);
        this._dispatchAction(ViewerConstants.VIEWER_ADD_ANNOTATION, annotation);

        
        this.resetDisplayMode();

        return annotation.ID;
    }
    async deleteMeasurement(measurement: Measurement) {
        if (!canPersistMeasurementDeletion(!!ViewerStore.getSelectedKnowledgeView(), this.canAuthorKnowledgeView(), measurement.saved)) return;
        {

            try {
                if (!SystemStore.isActionInProgress(ViewerConstants.VIEWER_DELETE_MEASUREMENT)) {
                    SystemActions.beginAction(ViewerConstants.VIEWER_DELETE_MEASUREMENT);
                    const view = ViewerStore.getSelectedViewImage()
                    await MeasurementServices.deleteMeasurement({
                        ID: measurement.id,
                        VersionToken: measurement.versionToken,
                    });

                }
            }
            finally {
                SystemActions.endAction(ViewerConstants.VIEWER_DELETE_MEASUREMENT);
            }
        }
    }

    async saveMeasurement(measurement: Measurement) {
        if (ViewerStore.isKnowledgeViewEditBusy() || !canSaveMeasurement(!!ViewerStore.getSelectedKnowledgeView(), ViewerStore.isEditingKnowledgeView(), measurement.saved)) return;
        if (!measurement.saved) {
            try {
                if (!SystemStore.isActionInProgress(ViewerConstants.VIEWER_SAVE_MEASUREMENT)) {
                    SystemActions.beginAction(ViewerConstants.VIEWER_SAVE_MEASUREMENT);
                    const view = ViewerStore.getSelectedViewImage()
                    const knowledgeView = ViewerStore.getSelectedKnowledgeView();
                    const apiResponse = await MeasurementServices.createMeasurement({
                        View360ID: view?.ID,
                        KnowledgeViewID: knowledgeView?.ID,
                        Type: measurement.measureMode.toString(),
                        Coordinates: measurement.measureMode == MeasurementType.Point ? [{ X: (measurement as any).startPosition.x, Y: (measurement as any).startPosition.y, Z: (measurement as any).startPosition.z }] : [{ X: (measurement as any).startPosition.x, Y: (measurement as any).startPosition.y, Z: (measurement as any).startPosition.z }, { X: (measurement as any).endPosition.x, Y: (measurement as any).endPosition.y, Z: (measurement as any).endPosition.z }],
                        VersionToken: '',
                        Name: measurement.name,
                        ID: ''
                    });

                    var savedMeasurement = apiResponse.Type == MeasurementType.Point
                        ? { dirty:false, checked: measurement.checked, name: apiResponse.Name, versionToken: apiResponse.VersionToken, saved: true, id: apiResponse.ID, measureMode: apiResponse.Type, startPosition: new BABYLON.Vector3((apiResponse as any).Coordinates[0].X, (apiResponse as any).Coordinates[0].Y, (apiResponse as any).Coordinates[0].Z) }
                        : { dirty:false, checked: measurement.checked, name: apiResponse.Name, versionToken: apiResponse.VersionToken, saved: true, measureMode: apiResponse.Type, startPosition: new BABYLON.Vector3((apiResponse as any).Coordinates[0].X, (apiResponse as any).Coordinates[0].Y, (apiResponse as any).Coordinates[0].Z), endPosition: new BABYLON.Vector3((apiResponse as any).Coordinates[1].X, (apiResponse as any).Coordinates[1].Y, (apiResponse as any).Coordinates[1].Z), distance: (measurement as any).distance, id: apiResponse.ID };

                    this._dispatchAction(ViewerConstants.VIEWER_RELOAD_MEASUREMENT, { oldid: measurement.id, measurement: savedMeasurement });

                    return apiResponse.ID;
                }
            }
            finally {
                SystemActions.endAction(ViewerConstants.VIEWER_SAVE_MEASUREMENT);
            }
        }
        else {
            
            try {
                if (!SystemStore.isActionInProgress(ViewerConstants.VIEWER_SAVE_MEASUREMENT)) {
                    SystemActions.beginAction(ViewerConstants.VIEWER_SAVE_MEASUREMENT);
                    const view = ViewerStore.getSelectedViewImage()
                    const knowledgeView = ViewerStore.getSelectedKnowledgeView();
                    const apiResponse = await MeasurementServices.editMeasurement({
                        View360ID: view?.ID,
                        KnowledgeViewID: knowledgeView?.ID,
                        Type: measurement.measureMode.toString(),
                        Coordinates: measurement.measureMode == MeasurementType.Point ? [{ X: (measurement as any).startPosition.x, Y: (measurement as any).startPosition.y, Z: (measurement as any).startPosition.z }] : [{ X: (measurement as any).startPosition.x, Y: (measurement as any).startPosition.y, Z: (measurement as any).startPosition.z }, { X: (measurement as any).endPosition.x, Y: (measurement as any).endPosition.y, Z: (measurement as any).endPosition.z }],
                        VersionToken: measurement.versionToken,
                        Name: measurement.name,
                        ID: measurement.id
                    });

                    var savedMeasurement = apiResponse.Type == MeasurementType.Point
                        ? { dirty:false, checked: measurement.checked, name: apiResponse.Name, versionToken: apiResponse.VersionToken, saved: true, id: apiResponse.ID, measureMode: apiResponse.Type, startPosition: new BABYLON.Vector3((apiResponse as any).Coordinates[0].X, (apiResponse as any).Coordinates[0].Y, (apiResponse as any).Coordinates[0].Z) }
                        : { dirty:false, checked: measurement.checked, name: apiResponse.Name, versionToken: apiResponse.VersionToken, saved: true, measureMode: apiResponse.Type, startPosition: new BABYLON.Vector3((apiResponse as any).Coordinates[0].X, (apiResponse as any).Coordinates[0].Y, (apiResponse as any).Coordinates[0].Z), endPosition: new BABYLON.Vector3((apiResponse as any).Coordinates[1].X, (apiResponse as any).Coordinates[1].Y, (apiResponse as any).Coordinates[1].Z), distance: (measurement as any).distance, id: apiResponse.ID };

                   
                    this._dispatchAction(ViewerConstants.VIEWER_RELOAD_MEASUREMENT, { oldid: measurement.id, measurement: savedMeasurement });

                    return apiResponse.ID;
                }
            }
            finally {
                SystemActions.endAction(ViewerConstants.VIEWER_SAVE_MEASUREMENT);
            }
        }
    }

    async editAnnotation(annotation: IViewAnnotationTag) {
        await AnnotationActions.editAnnotation(annotation);
        this._dispatchAction(ViewerConstants.VIEWER_EDIT_ANNOTATION, annotation);
    }

    async repositionAnnotation(annotation: IViewAnnotationTag, target3D: IVector3D) {
        if (!this.canAuthorKnowledgeView()) return;
        annotation.Target3D = target3D;
        await this.editAnnotation(annotation);
    }

    async deleteAnnotation(annotation: IViewAnnotationTag) {
        await AnnotationActions.deleteAnnotation(annotation);
        this._dispatchAction(ViewerConstants.VIEWER_DELETE_ANNOTATION, annotation);
    }

    async createTempAssetTag(target3D: IVector3D, cameraPosition: IVector3D) {
        if (!this.canAuthorFacilityContent()) return;
        const facility = FacilityStore.getSelectedFacility();
        const view = ViewerStore.getSelectedViewImage();
        const annotationType = ViewerStore.getAnnotationType();

        const selectedAsset = (ViewerStore.getSelectedAsset()) ? FacilityStore.getAssetById(ViewerStore.getSelectedAsset()) : null;
        const defaultTagText = selectedAsset?.Identity ?? "";
        let assetClass: IAssetClassDetail;
        
        if (selectedAsset?.AssetClass) {
            assetClass = selectedAsset.AssetClass;
        }
        else {
            if (annotationType == AnnotationType.AssetTag) {
                assetClass = facility.AssetClasses.find(ac => ac.Name.toLowerCase() == "pipeline") ?? facility.AssetClasses[0];
            } else if (annotationType == AnnotationType.AssetTagWithBox) {
                assetClass = facility.AssetClasses.find(ac => ac.Name.toLowerCase() == "generic asset") ?? facility.AssetClasses[0];
            }
        }

        let targetBoxMin: IVector3D, targetBoxMax: IVector3D;
        if (annotationType == AnnotationType.AssetTagWithBox) {
            const boxHalfDimension = 0.5;
            targetBoxMin = {
                X: target3D.X - boxHalfDimension,
                Y: target3D.Y - boxHalfDimension,
                Z: target3D.Z - boxHalfDimension
            }
            targetBoxMax = {
                X: target3D.X + boxHalfDimension,
                Y: target3D.Y + boxHalfDimension,
                Z: target3D.Z + boxHalfDimension
            }
        }

        this.editAssetTag({
            ID: null, // not used on create
            VersionToken: null, // not used on create
            AssetID: null,
            View360ID: view?.ID,
            Text: defaultTagText,
            Target2D: null,
            Target3D: target3D,
            TargetBoxMin: targetBoxMin,
            TargetBoxMax: targetBoxMax,
            CameraPosition: cameraPosition,
            AnnotationType: annotationType,
            AssetClass: assetClass,
            View: view,
        });
    }

    async createAssetTag(tag: IAssetTag) {
        if (!this.canAuthorFacilityContent()) return;
        const tagID = await AssetActions.createAssetTag(tag);

        await this.selectAnnotation(tagID);
        return tagID;
    }

    async repositionAssetTag(tag: IAssetTag, target3D: IVector3D, cameraPosition: IVector3D) {
        if (!this.canAuthorFacilityContent()) return;
        if (tag.AnnotationType == AnnotationType.AssetTagWithBox) {
            const xHalfSize = (tag.TargetBoxMax.X - tag.TargetBoxMin.X) / 2;
            const yHalfSize = (tag.TargetBoxMax.Y - tag.TargetBoxMin.Y) / 2;
            const zHalfSize = (tag.TargetBoxMax.Z - tag.TargetBoxMin.Z) / 2;
            
            tag.TargetBoxMin = {
                X: target3D.X - xHalfSize,
                Y: target3D.Y - yHalfSize,
                Z: target3D.Z - zHalfSize
            }
            tag.TargetBoxMax = {
                X: target3D.X + xHalfSize,
                Y: target3D.Y + yHalfSize,
                Z: target3D.Z + zHalfSize
            }
        }

        tag.Target3D = target3D;
        tag.CameraPosition = cameraPosition;

        return await AssetActions.editAssetTag(tag, false);
    }

    async createMeasurement(measurement: Measurement) {
        this._dispatchAction(ViewerConstants.VIEWER_ADD_MEASUREMENT, measurement);
    }

    async saveAssemblyInstance(assembly: IAssembly) {
        if (!this.canAuthorFacilityContent()) return;
        try {
            if (!SystemStore.isActionInProgress(ViewerConstants.VIEWER_SAVE_ASSEMBLY_INSTANCE)) {
                SystemActions.beginAction(ViewerConstants.VIEWER_SAVE_ASSEMBLY_INSTANCE);

                const facility = FacilityStore.getSelectedFacility();

                var form_data: any = new FormData();
                form_data.append("ID", '');
                form_data.append("Name", assembly.Name);
                form_data.append("AssemblyTemplateID", assembly.AssemblyTemplateID);
                form_data.append("FacilityID", facility.ID);
                form_data.append("VersionToken", '');

                if (assembly.KnowledgeViewID) {
                    form_data.append("KnowledgeViewID", assembly.KnowledgeViewID);
                }

                if (assembly.Position) {
                    form_data.append("Position.X", assembly.Position.X)
                    form_data.append("Position.Y", assembly.Position.Y)
                    form_data.append("Position.Z", assembly.Position.Z)
                }
                if (assembly.Orientation) {
                    form_data.append("Orientation.X", assembly.Orientation.X)
                    form_data.append("Orientation.Y", assembly.Orientation.Y)
                    form_data.append("Orientation.Z", assembly.Orientation.Z)
                }
                if (assembly.InstanceScale) {
                    form_data.append("InstanceScale.X", assembly.InstanceScale.X)
                    form_data.append("InstanceScale.Y", assembly.InstanceScale.Y)
                    form_data.append("InstanceScale.Z", assembly.InstanceScale.Z)
                }
                if (assembly.Color !== null && assembly.Color !== undefined) {
                    form_data.append("Color", assembly.Color.toString());
                }

                let res: IAssembly = await AssemblyServices.createAssembly(form_data);

                this.createAssemblyInstance(res);

                return res.ID;
            }
        }
        finally {
            SystemActions.endAction(ViewerConstants.VIEWER_SAVE_ASSEMBLY_INSTANCE);
        }
    }
    async createAssemblyInstances(assemblies: IAssembly[]) {
        this._dispatchAction(ViewerConstants.VIEWER_SET_ASSEMBLIES, assemblies);
    }

    async createMeasurements(measurements: IMeasurementApiData[]) {

        for (var x = 0; x < measurements.length; x++) {
            var measurement = measurements[x];

            var distance;

            var startPosition = new BABYLON.Vector3((measurement as any).Coordinates[0].X, (measurement as any).Coordinates[0].Y, (measurement as any).Coordinates[0].Z);
            var endPosition = measurement.Type == MeasurementType.Point ? null : new BABYLON.Vector3((measurement as any).Coordinates[1].X, (measurement as any).Coordinates[1].Y, (measurement as any).Coordinates[1].Z);

            if (measurement.Type == MeasurementType.Distance) {

                distance = (endPosition.subtract(startPosition).length());
            }
            else if (measurement.Type == MeasurementType.XOrtho) {

                var pB = new BABYLON.Vector3(endPosition.x, startPosition.y, startPosition.z);
                var pA = new BABYLON.Vector3(startPosition.x, startPosition.y, startPosition.z);

                distance = (pB.subtract(pA).length());
            }
            else if (measurement.Type == MeasurementType.YOrtho) {

                var pB = new BABYLON.Vector3(startPosition.x, endPosition.y, startPosition.z);
                var pA = new BABYLON.Vector3(startPosition.x, startPosition.y, startPosition.z);

                distance = (pB.subtract(pA).length());
            }
            else if (measurement.Type == MeasurementType.ZOrtho) {

                var pB = new BABYLON.Vector3(startPosition.x, startPosition.y, endPosition.z);
                var pA = new BABYLON.Vector3(startPosition.x, startPosition.y, startPosition.z);

                distance = (pB.subtract(pA).length());
            }

            var jsonMeasurement = measurement.Type == MeasurementType.Point
                ? { checked: true, name: measurement.Name, versionToken: measurement.VersionToken, saved: true, id: measurement.ID, measureMode: measurement.Type, startPosition: new BABYLON.Vector3((measurement as any).Coordinates[0].X, (measurement as any).Coordinates[0].Y, (measurement as any).Coordinates[0].Z) }
                : { checked: true, name: measurement.Name, versionToken: measurement.VersionToken, saved: true, measureMode: measurement.Type, startPosition: new BABYLON.Vector3((measurement as any).Coordinates[0].X, (measurement as any).Coordinates[0].Y, (measurement as any).Coordinates[0].Z), endPosition: new BABYLON.Vector3((measurement as any).Coordinates[1].X, (measurement as any).Coordinates[1].Y, (measurement as any).Coordinates[1].Z), distance: distance, id: measurement.ID };

            this.createMeasurement(jsonMeasurement as any);
        }
    }


    async createAssemblyInstance(assembly: IAssembly) {
        this._dispatchAction(ViewerConstants.VIEWER_ADD_ASSEMBLY, assembly);
    }
    async saveUpdatedAssetTag(updatedTagData: IAnnotationDisplayItem, resetMode: boolean = true) {
        //const updatedTagData = ViewerHelpers.safelyCastToAssetTag(ViewerStore.getUpdatedAnnotation());
        await AssetActions.editAssetTag(updatedTagData, false);
        if (resetMode) this.resetDisplayMode();
        //this._dispatchAction(ViewerConstants.VIEWER_UPDATE_ANNOTATION, null);
        
    }

    async resetUpdatedAssetTag() {
        
        const updatedTagData = ViewerHelpers.safelyCastToAssetTag(ViewerStore.getUpdatedAnnotation());
        if (updatedTagData) {
            await AssetActions.resetAssetTag(updatedTagData?.ID);
            this._dispatchAction(ViewerConstants.VIEWER_UPDATE_ANNOTATION, null);
        }
    }

    updateSelectedAssemblyBounds(min: IVector3D, max: IVector3D, scaling: IVector3D) {
        this._dispatchAction(ViewerConstants.VIEWER_UPDATE_SELECTED_ASSEMBLY_BOUNDS, {max, min, scaling});
    }
    updateAnnotationProperties(annotation: IAnnotationDisplayItem) {
        this._dispatchAction(ViewerConstants.VIEWER_UPDATE_ANNOTATION, annotation);
    }

    updateAssemblyProperties(assemblyID: string, position: IVector3D, rotation: IVector3D, scale: IVector3D) {
        this._dispatchAction(ViewerConstants.VIEWER_UPDATE_ASSEMBLY, {assemblyID, position, rotation, scale});
    }

    updateViewerSettings(name: string, value: string) {
        this._dispatchAction(ViewerConstants.VIEWER_UPDATE_SETTING, { name, value });
    }

    setMultipleSelectionEnabled(enabled: boolean) {
        this._dispatchAction(ViewerConstants.VIEWER_SET_MULTIPLE_SELECTION_ENABLED, enabled);
    }

    undoAssembly() {

        var view = ViewerStore.getSelectedKnowledgeView();
        if (view) {
            this._dispatchAction(ViewerConstants.UNDO_ASSEMBLY, view.Assemblies);
        }
        else {
            const facility = FacilityStore.getSelectedFacility();
            this._dispatchAction(ViewerConstants.UNDO_ASSEMBLY, facility.Assemblies);
        }
    }
    async saveAssembly(assembly: IAssembly) {
        const assemblies = ViewerStore.getAssemblies();
        const assemblyId = assembly.ID;
        const index = assemblies.findIndex(assembly => assembly.ID == assemblyId);
        if (index !== -1) {
            var form_data: any = new FormData();
            form_data.append("ID", assembly.ID);
            form_data.append("Name", assembly.Name);
            form_data.append("AssemblyTemplateID", assembly.AssemblyTemplateID);
            form_data.append("FacilityID", assembly.FacilityID);
            form_data.append("VersionToken", assembly.VersionToken);

            if (assembly.KnowledgeViewID) {
                form_data.append("KnowledgeViewID", assembly.KnowledgeViewID);
            }
            if (assembly.Position) {
                form_data.append("Position.X", Number(assembly.Position.X))
                form_data.append("Position.Y", Number(assembly.Position.Y))
                form_data.append("Position.Z", Number(assembly.Position.Z))
            }
            if (assembly.Orientation) {
                form_data.append("Orientation.X", Number(assembly.Orientation.X))
                form_data.append("Orientation.Y", Number(assembly.Orientation.Y))
                form_data.append("Orientation.Z", Number(assembly.Orientation.Z))
            }
            if (assembly.InstanceScale) {
                form_data.append("InstanceScale.X", Number(assembly.InstanceScale.X))
                form_data.append("InstanceScale.Y", Number(assembly.InstanceScale.Y))
                form_data.append("InstanceScale.Z", Number(assembly.InstanceScale.Z))
            }
            if (assembly.Color !== null && assembly.Color !== undefined) {
                form_data.append("Color", assembly.Color.toString());
            }

            const res = await AssemblyServices.updateAssembly(form_data)
            assemblies[index] = res;
            this._dispatchAction(ViewerConstants.UPDATE_ASSEMBLIES, assemblies);

            if (!assembly.KnowledgeViewID) {
                const facility = FacilityStore.getSelectedFacility();
                facility.Assemblies = assemblies;
                this._dispatchAction(FacilityConstants.UPDATE_FACILITY_DETAILS, facility);
            }
        }
    }
    async updateAssemblyInstance(assemblyID: string, name: string, color?: number | null) {
        try {
            if (!SystemStore.isActionInProgress(ViewerConstants.ASSEMBLY_INSTANCE_UPDATE)) {
                SystemActions.beginAction(ViewerConstants.ASSEMBLY_INSTANCE_UPDATE);
                const assemblies = ViewerStore.getAssemblies();
                const index = assemblies.findIndex(assembly => assembly.ID == assemblyID);
                if (index > -1) {
                    const assembly = assemblies[index];
                    var form_data: any = new FormData();
                    form_data.append("ID", assembly.ID);
                    form_data.append("Name", name);
                    form_data.append("AssemblyTemplateID", assembly.AssemblyTemplateID);
                    form_data.append("FacilityID", assembly.FacilityID);
                    form_data.append("VersionToken", assembly.VersionToken);

                    if (assembly.KnowledgeViewID) {
                        form_data.append("KnowledgeViewID", assembly.KnowledgeViewID);
                    }
                    if (assembly.Position) {
                        form_data.append("Position.X", Number(assembly.Position.X))
                        form_data.append("Position.Y", Number(assembly.Position.Y))
                        form_data.append("Position.Z", Number(assembly.Position.Z))
                    }
                    if (assembly.Orientation) {
                        form_data.append("Orientation.X", Number(assembly.Orientation.X))
                        form_data.append("Orientation.Y", Number(assembly.Orientation.Y))
                        form_data.append("Orientation.Z", Number(assembly.Orientation.Z))
                    }
                    if (assembly.InstanceScale) {
                        form_data.append("InstanceScale.X", Number(assembly.InstanceScale.X))
                        form_data.append("InstanceScale.Y", Number(assembly.InstanceScale.Y))
                        form_data.append("InstanceScale.Z", Number(assembly.InstanceScale.Z))
                    }
                    const updatedColor = color === undefined ? assembly.Color : color;
                    if (updatedColor !== null && updatedColor !== undefined) {
                        form_data.append("Color", updatedColor.toString());
                    }

                    const res = await AssemblyServices.updateAssembly(form_data)
                    assemblies[index] = res;
                    this._dispatchAction(ViewerConstants.UPDATE_ASSEMBLIES, assemblies);

                    if (!assembly.KnowledgeViewID) {
                        const facility = FacilityStore.getSelectedFacility();
                        facility.Assemblies = assemblies;
                        this._dispatchAction(FacilityConstants.UPDATE_FACILITY_DETAILS, facility);
                    }

                    return res;
                }
            }
        } finally {
            SystemActions.endAction(ViewerConstants.ASSEMBLY_INSTANCE_UPDATE);
        }
        
    }
    selectAnnotation(annotationID: string) {
        if (ViewerStore.getSelectedAssembly() != null) this.resetAssemblySelection();

        const assetTag = FacilityStore.getAssetTagById(annotationID);
        if (assetTag) {
            this._dispatchAction(ViewerConstants.VIEWER_SELECT, { annotationID, assetID: assetTag.AssetID });
        }
        else {
            this._dispatchAction(ViewerConstants.VIEWER_SELECT, { annotationID });
        }
    }

    selectAnnotations(
        ids?: string[],
        allSelectedIDs: Interfaces.Interfaces.ISelectedObjectInfo[] = [],
        unselectPrevious: boolean = false
    ): Interfaces.Interfaces.IMultiSelectionResult {
        if (!ids || ids.length === 0) {
            return {
                selected: allSelectedIDs || [],
                types: Array.from(
                    new Set((allSelectedIDs || []).map(x => x.objectType))
                )
            };
        }

        const selectedAnnotationIds = new Set<string>(
            (allSelectedIDs || [])
                .filter(x => x.objectType === Enumerations.PickObjectType.Annotation)
                .map(x => x.id)
        );

        const idsToSelect = new Set<string>();
        const idsToUnselect = new Set<string>();
        const processedIds = new Set<string>();

        for (const id of ids) {
            if (!id || processedIds.has(id)) {
                continue;
            }

            processedIds.add(id);

            if (selectedAnnotationIds.has(id)) {
                if (unselectPrevious) {
                    selectedAnnotationIds.delete(id);
                    idsToUnselect.add(id);
                }
                // else: already selected, leave it alone
            } else {
                selectedAnnotationIds.add(id);
                idsToSelect.add(id);
                idsToUnselect.delete(id);
            }
        }

        // Build payload from ALL currently selected annotations,
        // not just the newly selected ones.
        const allSelectedAnnotations: { annotationID: string; assetID?: string }[] = [];
        for (const annotationID of Array.from(selectedAnnotationIds)) {
            const assetTag = FacilityStore.getAssetTagById(annotationID);

            if (assetTag) {
                allSelectedAnnotations.push({
                    annotationID,
                    assetID: assetTag.AssetID
                });
            } else {
                allSelectedAnnotations.push({
                    annotationID
                });
            }
        }

        const finalAnnotationSelected: Interfaces.Interfaces.ISelectedObjectInfo[] = [];
        for (const annotationID of Array.from(selectedAnnotationIds)) {
            finalAnnotationSelected.push({
                id: annotationID,
                objectType: Enumerations.PickObjectType.Annotation
            });
        }

        if (idsToUnselect.size > 0) {
            ReactViewer3DActions.unhighlightObjects(
                this._viewerId,
                Enumerations.PickObjectType.Annotation,
                Array.from(idsToUnselect)
            );
        }

        // Dispatch the full selected annotation state
        this._dispatchAction(ViewerConstants.VIEWER_SELECT_ANNOTATIONS, {
            annotations: allSelectedAnnotations
        });

        if (idsToSelect.size > 0) {
            ReactViewer3DActions.highlightAssets(
                this._viewerId,
                Array.from(idsToSelect)
            );
        }

        const nonAnnotationSelected: Interfaces.Interfaces.ISelectedObjectInfo[] =
            (allSelectedIDs || []).filter(
                x => x.objectType !== Enumerations.PickObjectType.Annotation
            );

        const allRemainingSelected: Interfaces.Interfaces.ISelectedObjectInfo[] =
            nonAnnotationSelected.concat(finalAnnotationSelected);

        return {
            selected: allRemainingSelected,
            types: Array.from(new Set(allRemainingSelected.map(x => x.objectType)))
        };
    }

    selectAsset(assetID: string) {
        this._dispatchAction(ViewerConstants.VIEWER_SELECT, { assetID });
    }

    async selectHierarchyItem(
        id?: string,
        selectParent: boolean = false,
        highlight: boolean = true,
        repeatedSelectionBehavior: RepeatedSelectionBehavior = 'reselect'
    ) {
        if (id) {
            let hierarchyItemDetail: IHierarchyItemDetail = await HierarchyItemServices.getHierarchyItemDetail(id);

            if (selectParent) {
                const facilityAssetData = FacilityStore.getFacilityAssetsData();
                if (facilityAssetData?.NativeAssets?.length > 0) {
                    let currentItem: INamedItem = hierarchyItemDetail;
                    let parentIndex = hierarchyItemDetail.Parents.length - 1;
                    while (currentItem) {
                        if (facilityAssetData.NativeAssets.find(na => na.HierarchyItemID === currentItem.ID)) {
                            break;
                        }
                        else {
                            if (parentIndex >= 0) {
                                currentItem = hierarchyItemDetail.Parents[parentIndex];
                                parentIndex--;
                            }
                            else {
                                currentItem = null;
                            }
                        }
                    }

                    if (currentItem && currentItem != hierarchyItemDetail) {
                        hierarchyItemDetail = await HierarchyItemServices.getHierarchyItemDetail(currentItem.ID);
                    }
                }
            }

            hierarchyItemDetail.AssetID = FacilityStore.getFacilityAssetsData()?.NativeAssets?.find(na => na.HierarchyItemID === hierarchyItemDetail.ID)?.AssetID;
            hierarchyItemDetail.InternalAttributes?.forEach(attr => {
                // For now hierarchy item attributes are read-only
                attr.IsReadOnly = true;
            });

            const selectedHierarchyItems = ViewerStore.getSelectedHierarchyItems() || [];
            if (repeatedSelectionBehavior !== 'reselect'
                && selectedHierarchyItems.length === 1
                && selectedHierarchyItems[0]?.ID === hierarchyItemDetail.ID) {
                if (repeatedSelectionBehavior === 'unselect') {
                    ReactViewer3DActions.unhighlightAllObjects(this._viewerId);
                    this.resetHierarchyItemSelection();
                }
                return;
            }

            if (ViewerStore.getAllowMultiSelect()) {
                // if we're selecting ONE hierarchy item, we make sure to clear the selection before we notify
                // the viewer of a new selection. In multi selection, the viewer itself does not
                // clear previous selections automatically.
                ReactViewer3DActions.unhighlightAllObjects(this._viewerId);
            }

            this._dispatchAction(ViewerConstants.HIERARCHYITEM_SELECT, hierarchyItemDetail);

            if (highlight) {
                const data = await HierarchyItemServices.getAssetObjectIds(hierarchyItemDetail.ID);
                ReactViewer3DActions.highlightAssets(this._viewerId, data);
            }
        }
        else {
            this._dispatchAction(ViewerConstants.HIERARCHYITEM_SELECT);
        } 
    }

    async selectHierarchyItems(
        ids?: string[],
        allSelectedIDs: Interfaces.Interfaces.ISelectedObjectInfo[] = [], // intentionally unused
        clickedType: 'object' | 'scan' | 'smartmesh' | 'mesh' | 'annotation' | 'assembly' = 'object',
        selectParent: boolean = false,
        highlight: boolean = true,
        unselectPrevious: boolean = false
    ): Promise<Interfaces.Interfaces.IMultiSelectionResult> {
        void allSelectedIDs;

        if (clickedType === 'scan' || clickedType === 'assembly' || clickedType === 'annotation') {
            const existingHierarchyItems = ViewerStore.getSelectedHierarchyItems() || [];
            const finalHierarchySelected: Interfaces.Interfaces.ISelectedObjectInfo[] = existingHierarchyItems
                .filter(x => !!x && !!x.ID)
                .map(x => ({
                    id: x.ID,
                    objectType: Enumerations.PickObjectType.Object
                }));

            return {
                selected: finalHierarchySelected,
                types: Array.from(new Set(finalHierarchySelected.map(x => x.objectType)))
            };
        }

        const facilityAssetData = FacilityStore.getFacilityAssetsData();

        const selectionHierarchyCache = new Map<string, IHierarchyItemDetail>();
        const owningHierarchyCache = new Map<string, IHierarchyItemDetail>();
        const assetObjectIdsByHierarchyId = new Map<string, string[]>();

        const applyHierarchyItemMetadata = (hierarchyItemDetail: IHierarchyItemDetail): IHierarchyItemDetail => {
            if (!hierarchyItemDetail) {
                return hierarchyItemDetail;
            }

            hierarchyItemDetail.AssetID =
                facilityAssetData?.NativeAssets?.find(na => na.HierarchyItemID === hierarchyItemDetail.ID)?.AssetID;

            hierarchyItemDetail.InternalAttributes?.forEach(attr => {
                attr.IsReadOnly = true;
            });

            return hierarchyItemDetail;
        };

        const resolveOwningHierarchyItem = async (id: string): Promise<IHierarchyItemDetail | null> => {
            if (!id) {
                return null;
            }

            const cached = owningHierarchyCache.get(id);
            if (cached) {
                return cached;
            }

            let hierarchyItemDetail: IHierarchyItemDetail = await HierarchyItemServices.getHierarchyItemDetail(id);

            if (!hierarchyItemDetail) {
                return null;
            }

            if (facilityAssetData?.NativeAssets?.length > 0) {
                let currentItem: INamedItem = hierarchyItemDetail;
                let parentIndex = hierarchyItemDetail.Parents.length - 1;

                while (currentItem) {
                    if (facilityAssetData.NativeAssets.find(na => na.HierarchyItemID === currentItem.ID)) {
                        break;
                    } else if (parentIndex >= 0) {
                        currentItem = hierarchyItemDetail.Parents[parentIndex];
                        parentIndex--;
                    } else {
                        currentItem = null;
                    }
                }

                if (currentItem && currentItem.ID !== hierarchyItemDetail.ID) {
                    hierarchyItemDetail = await HierarchyItemServices.getHierarchyItemDetail(currentItem.ID);
                }
            }

            hierarchyItemDetail = applyHierarchyItemMetadata(hierarchyItemDetail);
            owningHierarchyCache.set(id, hierarchyItemDetail);

            return hierarchyItemDetail;
        };

        const resolveHierarchyItemForSelection = async (id: string): Promise<IHierarchyItemDetail | null> => {
            if (!id) {
                return null;
            }

            const cached = selectionHierarchyCache.get(id);
            if (cached) {
                return cached;
            }

            let hierarchyItemDetail: IHierarchyItemDetail | null;

            if (selectParent) {
                hierarchyItemDetail = await resolveOwningHierarchyItem(id);
            } else {
                hierarchyItemDetail = await HierarchyItemServices.getHierarchyItemDetail(id);

                if (!hierarchyItemDetail) {
                    return null;
                }

                hierarchyItemDetail = applyHierarchyItemMetadata(hierarchyItemDetail);
            }

            if (!hierarchyItemDetail) {
                return null;
            }

            selectionHierarchyCache.set(id, hierarchyItemDetail);
            return hierarchyItemDetail;
        };

        const getAssetObjectIdsForHierarchyItem = async (hierarchyItemID: string): Promise<string[]> => {
            if (!hierarchyItemID) {
                return [];
            }

            let assetObjectIds = assetObjectIdsByHierarchyId.get(hierarchyItemID);
            if (!assetObjectIds) {
                const data = await HierarchyItemServices.getAssetObjectIds(hierarchyItemID);
                assetObjectIds = Array.isArray(data) ? data : (data ? [data] : []);
                assetObjectIdsByHierarchyId.set(hierarchyItemID, assetObjectIds);
            }
            return assetObjectIds;
        };

        const getSelectionKey = (item: IHierarchyItemDetail): string => {
            return selectParent ? (item.AssetID || item.ID) : item.ID;
        };

        const expandHierarchyItemsToObjectIds = async (items: IHierarchyItemDetail[]): Promise<Set<string>> => {
            const result = new Set<string>();

            for (let i = 0; i < items.length; i++) {
                const item = items[i];

                if (!item || !item.ID) {
                    continue;
                }

                const objectIds = await getAssetObjectIdsForHierarchyItem(item.ID);
                for (let j = 0; j < objectIds.length; j++) {
                    result.add(objectIds[j]);
                }
            }

            return result;
        };

        const hierarchyItemContainsObjectId = async (
            hierarchyItem: IHierarchyItemDetail,
            objectId: string
        ): Promise<boolean> => {
            if (!hierarchyItem || !hierarchyItem.ID || !objectId) {
                return false;
            }

            const objectIds = await getAssetObjectIdsForHierarchyItem(hierarchyItem.ID);
            return objectIds.indexOf(objectId) >= 0;
        };

        const hierarchyItemContainsHierarchyItem = async (
            outerItem: IHierarchyItemDetail,
            innerItem: IHierarchyItemDetail
        ): Promise<boolean> => {
            if (!outerItem || !outerItem.ID || !innerItem || !innerItem.ID) {
                return false;
            }

            const outerObjectIds = await getAssetObjectIdsForHierarchyItem(outerItem.ID);
            const innerObjectIds = await getAssetObjectIdsForHierarchyItem(innerItem.ID);

            if (innerObjectIds.length === 0) {
                return false;
            }

            for (let i = 0; i < innerObjectIds.length; i++) {
                if (outerObjectIds.indexOf(innerObjectIds[i]) < 0) {
                    return false;
                }
            }

            return true;
        };

        const selectedHierarchyByKey = new Map<string, IHierarchyItemDetail>();

        (ViewerStore.getSelectedHierarchyItems() || []).forEach(existingItem => {
            if (!existingItem || !existingItem.ID) {
                return;
            }

            const itemWithMetadata = applyHierarchyItemMetadata(existingItem);
            selectedHierarchyByKey.set(getSelectionKey(itemWithMetadata), itemWithMetadata);
        });

        const beforeSelectedObjectIds = await expandHierarchyItemsToObjectIds(Array.from(selectedHierarchyByKey.values()));

        const processedIds = new Set<string>();
        const idsToProcess = ids || [];

        for (let i = 0; i < idsToProcess.length; i++) {
            const id = idsToProcess[i];

            if (!id || processedIds.has(id)) {
                continue;
            }

            processedIds.add(id);

            const selectionHierarchyItem = await resolveHierarchyItemForSelection(id);
            if (!selectionHierarchyItem) {
                continue;
            }

            const selectionKey = getSelectionKey(selectionHierarchyItem);

            const currentSelectedItems = Array.from(selectedHierarchyByKey.values());
            const overlappingKeys: string[] = [];
            let objectAlreadySelected = false;

            for (let j = 0; j < currentSelectedItems.length; j++) {
                const selectedItem = currentSelectedItems[j];
                if (!selectedItem || !selectedItem.ID) {
                    continue;
                }

                const containsClickedId = await hierarchyItemContainsObjectId(selectedItem, id);
                if (containsClickedId) {
                    objectAlreadySelected = true;
                }

                const clickedContainsSelected =
                    await hierarchyItemContainsHierarchyItem(selectionHierarchyItem, selectedItem);

                if (containsClickedId || clickedContainsSelected) {
                    overlappingKeys.push(getSelectionKey(selectedItem));
                }
            }

            const exactTargetAlreadySelected = selectedHierarchyByKey.has(selectionKey);

            if (!unselectPrevious) {
                if (objectAlreadySelected) {
                    continue;
                }

                selectedHierarchyByKey.set(selectionKey, selectionHierarchyItem);
                continue;
            }

            for (let j = 0; j < overlappingKeys.length; j++) {
                selectedHierarchyByKey.delete(overlappingKeys[j]);
            }

            if (exactTargetAlreadySelected && overlappingKeys.length === 1 && overlappingKeys[0] === selectionKey) {
                continue;
            }

            selectedHierarchyByKey.set(selectionKey, selectionHierarchyItem);
        }

        const resolvedSelectedItems = Array.from(selectedHierarchyByKey.values());

        this._dispatchAction(ViewerConstants.HIERARCHYITEMS_SELECT, resolvedSelectedItems);

        const afterSelectedObjectIds = await expandHierarchyItemsToObjectIds(resolvedSelectedItems);

        const idsToHighlight = new Set<string>();
        const idsToUnhighlight = new Set<string>();

        afterSelectedObjectIds.forEach(objectId => {
            if (!beforeSelectedObjectIds.has(objectId)) {
                idsToHighlight.add(objectId);
            }
        });

        beforeSelectedObjectIds.forEach(objectId => {
            if (!afterSelectedObjectIds.has(objectId)) {
                idsToUnhighlight.add(objectId);
            }
        });

        if (idsToUnhighlight.size > 0) {
            ReactViewer3DActions.unhighlightObjects(
                this._viewerId,
                Enumerations.PickObjectType.Object,
                Array.from(idsToUnhighlight)
            );
        }

        if (highlight && idsToHighlight.size > 0) {
            ReactViewer3DActions.highlightAssets(this._viewerId, Array.from(idsToHighlight));
        }

        const finalObjectType =
            clickedType === 'smartmesh'
                ? Enumerations.PickObjectType.GLBObject
                : clickedType === 'mesh'
                    ? Enumerations.PickObjectType.Mesh
                    : Enumerations.PickObjectType.Object;

        const finalHierarchySelected: Interfaces.Interfaces.ISelectedObjectInfo[] =
            Array.from(afterSelectedObjectIds).map(id => ({
                id,
                objectType: finalObjectType
            }));

        return {
            selected: finalHierarchySelected,
            types: Array.from(new Set(finalHierarchySelected.map(x => x.objectType)))
        };
    }

    async colorizeAssetSets(assetSetDefs: {
        id: string;
        priority: number;
        colorKey?: number;
        hidden?: boolean;
        isAll3DAssets?: boolean;
        materialID?: string | null;
        tagIDs: string[];
    }[], strictIsolation: boolean = false) {
        const defs = assetSetDefs || [];

        const requestId = `${Date.now()}-${Math.random().toString(16).slice(2)}`;
        const ackPromise = ReactViewer3DActions.waitForAssetSetsColorized(this._viewerId, requestId);

        ReactViewer3DActions.colorizeAssetSets(
            this._viewerId,
            defs,
            requestId,
            strictIsolation
        );

        await ackPromise;
    }

    setAdvancedMaterialTunings(tunings: Record<string, IAdvancedMaterialTuning> = {}) {
        ReactViewer3DActions.setAdvancedMaterialTunings(this._viewerId, tunings);
    }

    destroyAllAssetSets() {
        this._assetSetPopulationGeneration++;
        this._activeAssetSetMemberships.clear();
        ReactViewer3DActions.destroyAllAssetSets(this._viewerId);
    }

    prepareForOntologyVisualizationClear() {
        if (!this._viewerId) return;
        this._assetSetPopulationGeneration++;
        this._activeAssetSetMemberships.clear();
        ReactViewer3DActions.invalidateAssetSetOctreeUpdates(this._viewerId);
    }

    invalidateAssetSetMembership(assetSetID: string) {
        this._assetSetMembershipCache.invalidate(assetSetID);
        this._assetSetPopulationGeneration++;
        this._activeAssetSetMemberships.delete(assetSetID.trim().toLocaleLowerCase());
    }

    invalidateAllAssetSetMemberships() {
        this._assetSetMembershipCache.clear();
        this._assetSetPopulationGeneration++;
        this._activeAssetSetMemberships.clear();
    }

    cancelPendingAssetSetPopulation() {
        this._assetSetPopulationGeneration++;
    }

    getPendingAssetSetPopulationIds(assetSets: IAssetSet[], selectedOntologyID?: string | null): string[] {
        return (assetSets || [])
            .filter(assetSet => assetSet.Type !== AssetSetType.All3DAssets && assetSet.Type !== AssetSetType.AllAssetTags)
            .filter(assetSet => {
                const active = this._activeAssetSetMemberships.get(assetSet.ID.trim().toLocaleLowerCase());
                return !active || active.signature !== getAssetSetPopulationSignature(assetSet, selectedOntologyID);
            })
            .map(assetSet => assetSet.ID);
    }

    async populateAssetSets(assetSets: IAssetSet[], selectedOntologyID?: string | null) {
        const facilityIDs = Array.from(new Set(
            (assetSets ?? []).map(assetSet => assetSet.FacilityID).filter(Boolean)
        ));
        if (facilityIDs.length === 1) {
            const facilityID = facilityIDs[0].trim().toLocaleLowerCase();
            if (this._assetSetMembershipFacilityID &&
                this._assetSetMembershipFacilityID !== facilityID) {
                this._assetSetMembershipCache.clear();
                this._activeAssetSetMemberships.clear();
            }
            this._assetSetMembershipFacilityID = facilityID;
        }

        const populationGeneration = ++this._assetSetPopulationGeneration;
        const serverAssetSets = (assetSets ?? []).filter(assetSet => assetSet.Type !== AssetSetType.AllAssetTags);
        const { deactivatedSetIds, setsToActivate } = planAssetSetMembershipDelta(
            serverAssetSets,
            this._activeAssetSetMemberships,
            selectedOntologyID,
        );
        let activatedSets: { id: string; assetIds: string[]; isAll3DAssets: boolean }[] = [];
        const failedAssetSetIds: string[] = [];

        if (setsToActivate.length > 0) {
            const explicitAssetSets = setsToActivate.filter(assetSet =>
                assetSet.Type !== AssetSetType.All3DAssets && assetSet.Type !== AssetSetType.AllAssetTags);
            const facilities = Array.from(new Set(
                explicitAssetSets.map(assetSet => assetSet.FacilityID).filter(Boolean)
            ));

            if (facilities.length > 1) {
                throw new Error('Asset Set memberships from multiple Facilities cannot be populated together.');
            }
            if (explicitAssetSets.length > 0 && !selectedOntologyID) {
                throw new Error('An ontology is required to load Asset Set memberships.');
            }

            const scopedAssetSets = explicitAssetSets.map(assetSet => ({
                ...assetSet,
                ScopeToken: selectedOntologyID,
            }));

            const resolvedMemberships = explicitAssetSets.length > 0
                ? await this._assetSetMembershipCache.resolveManySettled(
                    scopedAssetSets,
                    async missingAssetSets => {
                        if (facilities.length !== 1) {
                            throw new Error('A Facility is required to load Asset Set memberships.');
                        }

                        const rows = await loadInBoundedBatches(
                            missingAssetSets,
                            batch => AssetSetsServices.getAssetObjectChildrenBatch(
                                facilities[0],
                                selectedOntologyID!,
                                batch.map(assetSet => assetSet.ID)
                            )
                        );

                        return rows.map(row => ({
                            ID: row.AssetSetID,
                            VersionToken: row.VersionToken,
                            AssetObjectIDs: row.AssetObjectIDs,
                        }));
                    }
                )
                : [];
            const membershipsByID = new Map<string, string[]>();
            resolvedMemberships.forEach((resolution, index) => {
                const assetSet = explicitAssetSets[index];
                if (resolution.status === 'fulfilled') {
                    membershipsByID.set(assetSet.ID.trim().toLocaleLowerCase(), resolution.value);
                } else {
                    failedAssetSetIds.push(assetSet.ID);
                    console.error(`Unable to populate Asset Set '${assetSet.ID}'.`, resolution.reason);
                }
            });
            activatedSets = setsToActivate.flatMap<{
                id: string;
                assetIds: string[];
                isAll3DAssets: boolean;
            }>(assetSet => {
                if (assetSet.Type === AssetSetType.All3DAssets) {
                    return [{ id: assetSet.ID, isAll3DAssets: true, assetIds: ["__ALL__"] }];
                }

                const normalizedID = assetSet.ID.trim().toLocaleLowerCase();
                const membership = membershipsByID.get(normalizedID);
                if (membership) {
                    return [{
                        id: assetSet.ID,
                        isAll3DAssets: false,
                        assetIds: membership,
                    }];
                }

                // A scoped refresh failure must replace any membership retained
                // from a previous ontology before geometry can resume.
                return failedAssetSetIds.includes(assetSet.ID) ? [{
                    id: assetSet.ID,
                    isAll3DAssets: false,
                    assetIds: [],
                }] : [];
            });
        }

        // Membership requests can resolve out of order while switching views.
        // Only the newest population may replace the viewer's current sets.
        if (populationGeneration !== this._assetSetPopulationGeneration) {
            return { activatedAssetSetIds: [], failedAssetSetIds: [] };
        }

        if (!activatedSets.length && !deactivatedSetIds.length) {
            return { activatedAssetSetIds: [], failedAssetSetIds };
        }

        // Create requestId ourselves so we can start waiting before dispatch
        const requestId = `${Date.now()}-${Math.random().toString(16).slice(2)}`;

        const ackPromise = ReactViewer3DActions.waitForAssetSetsPopulated(this._viewerId, requestId);

        ReactViewer3DActions.applyAssetSetMembershipDelta(
            this._viewerId,
            activatedSets,
            deactivatedSetIds,
            requestId
        );

        for (const assetSetId of deactivatedSetIds) {
            this._activeAssetSetMemberships.delete(assetSetId.trim().toLocaleLowerCase());
        }
        for (const assetSetId of failedAssetSetIds) {
            this._activeAssetSetMemberships.delete(assetSetId.trim().toLocaleLowerCase());
        }
        const failedIds = new Set(failedAssetSetIds.map(assetSetId => assetSetId.trim().toLocaleLowerCase()));
        const activatedIds = new Set(activatedSets
            .map(assetSet => assetSet.id.toLocaleLowerCase())
            .filter(assetSetId => !failedIds.has(assetSetId)));
        for (const assetSet of setsToActivate.filter(assetSet =>
            activatedIds.has(assetSet.ID.toLocaleLowerCase()))) {
            this._activeAssetSetMemberships.set(assetSet.ID.trim().toLocaleLowerCase(), {
                id: assetSet.ID,
                signature: getAssetSetPopulationSignature(assetSet, selectedOntologyID)
            });
        }

        await ackPromise;
        return {
            activatedAssetSetIds: activatedSets
                .filter(assetSet => !failedIds.has(assetSet.id.trim().toLocaleLowerCase()))
                .map(assetSet => assetSet.id),
            failedAssetSetIds,
        };
    }


    async displayKVAssetSets(assetSets: IKnowledgeViewAssetSet[]) {
        // If there are no sets, just tell the viewer "nothing"
        if (!assetSets || assetSets.length === 0) {
            ReactViewer3DActions.displayKVAssetSets(this._viewerId, []);
            return;
        }

        ReactViewer3DActions.displayKVAssetSets(this._viewerId, assetSets);
    }

    focusHierarchyItem(id: string) {
        this._dispatchAction(ViewerConstants.VIEWER_FOCUS_HIERARCHY_ITEM, { id });
    }
    async getHierarchyItemExtents(id: string) {
        return await HierarchyItemServices.getAssetExtent(id);
    }
    resetHierarchyItemSelection() {
        this._dispatchAction(ViewerConstants.HIERARCHYITEM_SELECT, null);
    }
    selectMeshItem(id?: string) {
        // placeholder for mesh item selection
        let meshItemDetail = id;

        this._dispatchAction(ViewerConstants.MESHITEM_SELECT, meshItemDetail);
    }
    resetMeshItemSelection() {
        this._dispatchAction(ViewerConstants.MESHITEM_SELECT, null);
    }
    selectAssembly(assemblyID: string, boundingInfo: IAssemblyBounds) {
        if (ViewerStore.getSelectedAnnotation() != null) 
            this.resetAnnotation();
        this._dispatchAction(ViewerConstants.VIEWER_SELECT_ASSEMBLY, { assemblyID, boundingInfo });
    }
    resetAssemblySelection(undoDirtyAssembly: boolean = true) {
        if (undoDirtyAssembly && ViewerStore.isAssemblyDirty()) {
            this.undoAssembly();
        }
        this._dispatchAction(ViewerConstants.VIEWER_SELECT_ASSEMBLY, { assemblyID: null, boundingInfo: null });
    }

    depthMapLoaded(occludedAnnotations: IAnnotationDisplayItem[]) {
        this._dispatchAction(ViewerConstants.VIEWER_DEPTH_LOADED, occludedAnnotations);
    }

    setMiniMapOpen(open: boolean) {
        this._dispatchAction(ViewerConstants.VIEWER_MINI_MAP_OPEN, open);
    }
    setMiniMapPositions(positions: PositionDataModel) {
        this._dispatchAction(ViewerConstants.VIEWER_MINI_MAP_POSITIONS, positions);
    }
    setMiniMapSize(size: any) {
         this._dispatchAction(ViewerConstants.VIEWER_MINI_MAP_SIZE, size);
    }
    setMiniMapFullScreen(mode: boolean) {
        this._dispatchAction(ViewerConstants.VIEWER_MINI_MAP_FULL_SCREEN, mode);
    }

    setAnnotationEditorMode(mode: boolean) {
        this._dispatchAction(ViewerConstants.SET_ANNOTATION_EDITOR_MODE, mode);
    }
    resetAnnotation() {
        this._dispatchAction(ViewerConstants.VIEWER_RESET_ANNOTATION);
    }
    private async _openKnowledgeViewInternal(kvID: string, lodData?: string) {
        let kvDetail = await KnowledgeBookActions.getKnowledgeViewDetail(kvID);
        await this._dispatchAction(ViewerConstants.KNOWLEDGE_VIEW_OPEN, { kv: kvDetail, lodData });
        await this.createAssemblyInstances(kvDetail.Assemblies);
        await this.createMeasurements(kvDetail.Measurements);
        return kvDetail;
    }

    private _checkDepthMapLoaded() {
        
        const isDepthMapLoaded = ViewerStore.isDepthMapLoaded();
        if (!isDepthMapLoaded) {
            SystemActions.displayInfoMessage({
                message: InovxLoader.t('facility:message.depthMapLoading'),
            });
        }
        return isDepthMapLoaded;
    }

    private _setSelectedAssemblyCreationTemplate(template: IAssemblyTemplate) {
        this._dispatchAction(ViewerConstants.VIEWER_SET_ASSEMBLY_CREATION_TEMPLATE, template);
    }
    private _setDisplayMode(mode: DisplayMode, annotationType?: AnnotationType, measurementType?: MeasurementType, assemblyID?: string, annotationClassID?: string) {
        this._dispatchAction(ViewerConstants.VIEWER_SET_DISPLAY_MODE, { mode, annotationType, measurementType, assemblyID, annotationClassID });
    }

    private _setMeasurementType(measurementType?: MeasurementType) {
        this._dispatchAction(ViewerConstants.VIEWER_SET_MEASUREMENT_TYPE, measurementType);
    }

    private _getClassNameLower(annotationType: AnnotationType) {
        return (annotationType == AnnotationType.ObservationTag) ? "observationtag"
            : (annotationType == AnnotationType.InformationAnnotation) ? "informationannotation"
                : (annotationType == AnnotationType.LinkAnnotation) ? "linkannotation"
                    : null;
    }

    async getAttributesInformation(assetId: string) {
        return await AssetActions.getAttributesInformation(assetId);
    }
    resetAttributesInformation() {
        if (ViewerStore.getAttributesInformation()) {
            this._dispatchAction(ViewerConstants.SET_ATTRIBUTES_INFORMATION, null);
        }
    }

    editAssetTag(tag: IAssetTag) {
        this._dispatchAction(ViewerConstants.VIEWER_EDIT_ASSET_TAG, tag);
    }
    changeAssetPanelVisibility(isVisible: boolean = false) {
        this._dispatchAction(ViewerConstants.ASSET_PANEL_VISIBILITY, isVisible);
    }
    setAssemblyTemplate(assemblyTemplate: IAssemblyTemplate) {
        if (assemblyTemplate && !this.canAuthorFacilityContent()) return;
        this._dispatchAction(ViewerConstants.SET_SELECTED_ASSEMBLY_TEMPLATE, assemblyTemplate );
    }

    getCoreKeys(ontologyTypes: string[]) {
        const ONTOLOGY_TO_KEY: Record<string, keyof ContentLayers> = {
            'CAD Data': 'cad',
            'Point Cloud Data': 'pointCloud',
            'Mesh Data': 'mesh',
            'Assets': 'assets'
        };

        const keys = ontologyTypes.map((t) => ONTOLOGY_TO_KEY[t]).filter(Boolean) as (keyof ContentLayers)[];
        const order: (keyof ContentLayers)[] = ['assets', 'cad', 'pointCloud', 'mesh'];
        return order.filter((k) => keys.includes(k));
    }

    setContentLayers(contentLayers: ContentLayers) {
        const displayMode = ViewerStore.getDisplayMode();
        const assemblyCreationTemplate = ViewerStore.getAssemblyCreationTemplate();

        if (!contentLayers.assemblies) {
            if (assemblyCreationTemplate || displayMode == DisplayMode.CreateAssembly || displayMode == DisplayMode.AssemblyVolumeMove || displayMode == DisplayMode.AssemblyVolumeScale) {
                this.resetDisplayMode();
            }
            this.resetAssemblySelection();
        }
        if (!contentLayers.assetTags && (displayMode == DisplayMode.CreateAnnotation || displayMode == DisplayMode.PositionAnnotation || displayMode == DisplayMode.TagVolumeMove || displayMode == DisplayMode.TagVolumeScale)) {
            this.resetDisplayMode();
        }
        
        this._dispatchAction(ViewerConstants.SET_CONTENT_LAYERS, contentLayers);
    }
    setLayerVisible(key: keyof ReturnType<typeof ViewerStore.getContentLayers>) {
        const layers = ViewerStore.getContentLayers();
        if (key === 'assetTags') {
            this.setVisibleAssetTagClasses(null);
        }
        const newLayers = { ...layers, [key]: true };

        this.setContentLayers(newLayers);
    }
    toggleLayer(key: keyof ReturnType<typeof ViewerStore.getContentLayers>) {
        const layers = ViewerStore.getContentLayers();
        const newLayers = { ...layers, [key]: !layers[key] };

        this.setContentLayers(newLayers);
    }

    async deleteAssembly(assemblyID: string) {
        const assembly = ViewerStore.getAssemblies().find(assembly => assembly.ID == assemblyID);
        if (assembly) {
            await AssemblyServices.deleteAssembly(assembly);
            this._dispatchAction(ViewerConstants.VIEWER_REMOVE_ASSEMBLY, assemblyID);
        }
    }

    set3DCameraProperties(position: IVector3D, target: IVector3D) {
        this._dispatchAction(ViewerConstants.VIEWER_SET_3DCAMERA_PROPERTIES, { position, target });
    }

    async selectAssetSet(assetSet: IAssetSet) {
        this._dispatchAction(ViewerConstants.ASSET_SET_SELECT, assetSet);
    }
    resetSelectedAssetSet() {
        this._dispatchAction(ViewerConstants.ASSET_SET_SELECT, null);
    }

    async changeKnowledgeBookViewingMode(mode: KnowledgeBookViewingMode, shouldRestore: boolean = true) {
        if (mode === 'manageAssets' && this.checkForKnowledgeViewChanges(() => this.changeKnowledgeBookViewingMode(mode, shouldRestore))) return;
        const selectedKnowledgeBook = KnowledgeBookStore.getSelectedKnowledgeBook();
        if (selectedKnowledgeBook?.ID) {
            if (mode === 'manageAssets') {
                this.closeKnowledgeView(true);
                this.setContentLayers({ assets: true, assetTags: true, pointCloud: false, cad: false, positions360: false, scanPositions: true, colorScanPositions: true, blackAndWhiteScanPositions: true, imagePositions: true, assemblies: false, mesh: false });

            } else {
                if (shouldRestore) {
                    // Restore previously opened knowledge view if any
                    const savedKV = ViewerStore.getSavedKnowledgeView();
                    if (savedKV) {
                        await this.openKnowledgeView(savedKV);
                    }
                }
            }
        }

        KnowledgeBookActions.setKnowledgeBookViewingMode(mode);
    }

    checkForKnowledgeViewChanges(continuation?: EditContinuation): boolean {
        return this._editSession.block(continuation);
    }

    confirmDeleteAssetTag(tag: IAssetTag) {
        const facility = FacilityStore.getSelectedFacility();
        const asset = AssetActions.createAssetTagsTree(facility).find((a) =>
            a.Tags.some((t: IAssetTag) => t.ID === tag.ID)
        );
        if (asset?.Source == AssetSourceType.VPlant && asset.Tags.length === 1) {
            const messageObject = {
                message: InovxLoader.t('facility:message.confirmDeleteAssetWithTag', { className: tag.AssetClass?.Name ?? '', tagName: tag.Text }),
                confirmCallback: async () => {
                    await AssetActions.deleteAssetTag(tag, true);
                    await this.resetAnnotation();
                },
                cancelCallback: () => { }
            };
            SystemActions.displayYesNoMessage(messageObject);
        } else {
            SystemActions.displayConfirmationMessage({
                message: InovxLoader.t('facility:message.confirmDeleteTag'),
                confirmCallback: async () => {
                    await AssetActions.deleteAssetTag(tag, false);
                    await this.resetAnnotation();
                }
            });
        }
    }

    private _dispatchAction(actionType: string, data?: any) {
        Inovx.Shared.Flux.AppDispatcher.dispatch({
            actionType: actionType,
            data: data
        });
    }
}

const viewerActions = new ViewerActions();

export default viewerActions;
